import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEstimate } from "@/context/EstimateContext";
import { PURCHASE_TIERS } from "@/data/presets";
import { formatUSD, formatNum } from "@/lib/format";

export function AddPurchaseDialog({ open, onOpenChange, editing }) {
  const { addPurchase, updatePurchase } = useEstimate();
  const [label, setLabel] = useState("");
  const [coins, setCoins] = useState("");
  const [dollars, setDollars] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (editing) {
      setLabel(editing.label);
      setCoins(String(editing.coins));
      setDollars(String(editing.dollars));
    } else {
      setLabel("");
      setCoins("");
      setDollars("");
    }
  }, [open, editing]);

  const pickTier = (t) => {
    setLabel(t.label);
    setCoins(String(t.coins));
    setDollars(String(t.dollars));
    setError("");
  };

  const save = () => {
    const numCoins = parseInt(coins, 10);
    const numDollars = parseFloat(dollars);
    if (!Number.isFinite(numCoins) || numCoins <= 0) return setError("Enter coins purchased (> 0).");
    if (!Number.isFinite(numDollars) || numDollars < 0) return setError("Enter the amount paid.");
    const finalLabel = label.trim() || `${formatNum(numCoins)} credits`;
    if (editing) {
      updatePurchase(editing.id, { label: finalLabel, coins: numCoins, dollars: numDollars });
    } else {
      addPurchase({ label: finalLabel, coins: numCoins, dollars: numDollars });
    }
    onOpenChange(false);
  };

  const perCoin =
    parseInt(coins, 10) > 0 && parseFloat(dollars) >= 0
      ? `$${(parseFloat(dollars) / parseInt(coins, 10)).toFixed(4)}/coin`
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface-2 border-border max-h-[88vh] overflow-y-auto"
        data-testid="add-purchase-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editing ? "Edit Purchase" : "Log Credit Purchase"}
          </DialogTitle>
          <DialogDescription className="sr-only">Log coins purchased and amount paid.</DialogDescription>
        </DialogHeader>

        {!editing && (
          <div className="grid grid-cols-2 gap-2">
            {PURCHASE_TIERS.map((t) => (
              <button
                key={t.id}
                data-testid={`tier-${t.id}`}
                onClick={() => pickTier(t)}
                className="relative rounded-xl border border-border bg-surface-3 p-3 text-left transition-colors hover:border-brand"
              >
                {t.badge && (
                  <span className="absolute right-2 top-2 rounded-full bg-brand-tint px-1.5 py-0.5 text-[10px] font-semibold text-brand">
                    {t.badge}
                  </span>
                )}
                <p className="font-mono font-bold text-foreground tabular">{formatNum(t.coins)}</p>
                <p className="text-xs text-muted-foreground">{formatUSD(t.dollars)}</p>
              </button>
            ))}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Label (optional)</label>
            <Input
              data-testid="input-purchase-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Starter pack"
              className="mt-2 bg-surface border-border"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Coins</label>
              <Input
                data-testid="input-purchase-coins"
                value={coins}
                inputMode="numeric"
                onChange={(e) => {
                  setCoins(e.target.value.replace(/[^0-9]/g, ""));
                  setError("");
                }}
                placeholder="0"
                className="mt-2 bg-surface border-border font-mono"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Paid (USD)</label>
              <Input
                data-testid="input-purchase-dollars"
                value={dollars}
                inputMode="decimal"
                onChange={(e) => {
                  setDollars(e.target.value.replace(/[^0-9.]/g, ""));
                  setError("");
                }}
                placeholder="0.00"
                className="mt-2 bg-surface border-border font-mono"
              />
            </div>
          </div>
          {perCoin && (
            <p className="font-mono text-sm text-brand tabular" data-testid="purchase-per-coin">
              = {perCoin}
            </p>
          )}
          {error && (
            <p className="text-sm text-danger" data-testid="purchase-error">
              {error}
            </p>
          )}
          <Button
            data-testid="save-purchase-button"
            onClick={save}
            className="w-full bg-brand hover:bg-brand-dark text-white font-semibold"
          >
            {editing ? "Save Changes" : "Add Purchase"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
